import {
  flagColorMap,
  type FlagAdvice,
  type FlagColor,
  type FlagKey,
  type FlagTracking,
} from './flagTypes'
import { hiringIssueDisplayMap, hiringIssueMap } from './hiringIssueTypes'

export const inconsistentProcessRedAdvice: FlagAdvice = {
  title: 'Inconsistent Process',
  impact:
    'Unpredictable hiring timelines, frustrated stakeholders, and inconsistent candidate evaluation.',
  recommendations: [
    'Create a standardized, documented process with clear ownership for each step',
    'Train all stakeholders on the standardized process',
    'Create a feedback mechanism for continuous process improvement',
  ],
}

export const inconsistentProcessGreenAdvice: FlagAdvice = {
  title: 'Clear Process',
  impact:
    'Clear processes enable predictable timelines and consistent hiring results.',
  recommendations: [
    'Document your processes thoroughly for new team member onboarding',
    'Conduct periodic process reviews to identify optimization opportunities',
    'Implement process compliance metrics to ensure adherence',
  ],
}

export const lackOfAutomationYellowAdvice: FlagAdvice = {
  title: 'Lack of Automation',
  impact:
    'Wasted recruiter time, delayed communications, and inconsistent candidate experience.',
  recommendations: [
    'Implement an ATS with workflow automation capabilities',
    'Use calendar integration tools for automated interview scheduling',
    'Create templates for common communications',
  ],
}

export const lackOfAutomationGreenAdvice: FlagAdvice = {
  title: 'Automation of Repetitive Tasks',
  impact:
    'Your team focuses on high-value activities while technology handles repetitive tasks.',
  recommendations: [
    'Regularly review automation tools for new features and capabilities',
    'Calculate and communicate time savings from automation to leadership',
    'Up-skill recruiters to manage and enhance automation tools',
  ],
}

export const longCyclesWithDropoffRedAdvice: FlagAdvice = {
  title: 'Long Hiring Cycles',
  impact:
    "You're losing top talent to competitors who move faster. Studies show that 57% of job seekers lose interest if the hiring process is too lengthy.",
  recommendations: [
    'Conduct a time audit of your current hiring process to identify specific delays',
    'Establish clear SLAs for each hiring stage (e.g., 48 hours for resume screening, 5 days to complete interviews)',
    'Reduce approval layers for moving candidates forward',
  ],
}

export const longCyclesWithDropoffGreenAdvice: FlagAdvice = {
  title: 'Quick Hiring Cycles',
  impact:
    'Your efficient hiring process gives you a competitive edge in securing top talent.',
  recommendations: [
    'Continue monitoring time-to-hire metrics and benchmark against industry standards',
    'Regularly solicit feedback from recent hires about their experience with the process',
    'Implement predictive analytics to identify when your process is starting to slow down',
  ],
}

export const poorStakeholderCommunicationRedAdvice: FlagAdvice = {
  title: 'Poor Communication or Misalignment',
  impact:
    'Misalignment leads to contradictory information to candidates, repeated questions, and inefficient decision-making.',
  recommendations: [
    'Implement a structured intake process with hiring managers before opening positions',
    'Schedule weekly brief alignment meetings between recruiters and hiring managers',
    'Develop standardized scorecards for evaluation and set up interview training',
  ],
}

export const poorStakeholderCommunicationGreenAdvice: FlagAdvice = {
  title: 'Great Coordination',
  impact:
    'Well-coordinated teams deliver a great candidate experience, make decisions efficiently and have a positive impact on quality of hire.',
  recommendations: [
    'Implement advanced collaborative tools for centralized candidate evaluation',
    'Create a knowledge base of common questions and decision criteria',
    'Coach new hiring managers and recruiters on effective communication practices',
  ],
}

export const schedulingConflictsYellowAdvice: FlagAdvice = {
  title: 'Scheduling Conflicts',
  impact:
    'Extended time-to-hire, high candidate drop-off, and poor candidate experience',
  recommendations: [
    "Block dedicated interview time slots on hiring managers' calendars each week",
    "Implement self-scheduling tools that sync with interviewers' calendars",
    'Create a pool of backup interviewers trained on the role requirements',
  ],
}

export const schedulingConflictsGreenAdvice: FlagAdvice = {
  title: 'Calendar Management',
  impact:
    'Smooth scheduling creates a positive candidate experience, short time to hire and can be a competitive advantage!',
  recommendations: [
    'Share your scheduling best practices across the organization',
    'Monitor and celebrate metrics around interview scheduling efficiency',
    'Continuously refine your scheduling tools and processes',
  ],
}

const flagKeyToFlagColor: Record<FlagKey, Record<any, FlagColor>> = {
  [hiringIssueMap.inconsistentProcess]: {
    true: flagColorMap.red,
    false: flagColorMap.green,
  },
  [hiringIssueMap.lackOfAutomation]: {
    true: flagColorMap.yellow,
    false: flagColorMap.green,
  },
  [hiringIssueMap.longCyclesWithDropoff]: {
    true: flagColorMap.red,
    false: flagColorMap.green,
  },
  [hiringIssueMap.poorStakeholderCommunication]: {
    true: flagColorMap.red,
    false: flagColorMap.green,
  },
  [hiringIssueMap.schedulingConflicts]: {
    true: flagColorMap.yellow,
    false: flagColorMap.green,
  },
}

export type AuditFlagMap = Record<
  FlagColor,
  Record<FlagKey, { advice: FlagAdvice | null; questions: string[] }>
>

export const defaultFlagMap: AuditFlagMap = {
  [flagColorMap.red]: {
    [hiringIssueMap.inconsistentProcess]: {
      advice: inconsistentProcessRedAdvice,
      questions: [] as string[],
    },
    [hiringIssueMap.lackOfAutomation]: {
      advice: null,
      questions: [] as string[],
    },
    [hiringIssueMap.longCyclesWithDropoff]: {
      advice: longCyclesWithDropoffRedAdvice,
      questions: [] as string[],
    },
    [hiringIssueMap.poorStakeholderCommunication]: {
      advice: poorStakeholderCommunicationRedAdvice,
      questions: [] as string[],
    },
    [hiringIssueMap.schedulingConflicts]: {
      advice: null,
      questions: [] as string[],
    },
  },
  [flagColorMap.yellow]: {
    [hiringIssueMap.inconsistentProcess]: {
      advice: null,
      questions: [] as string[],
    },
    [hiringIssueMap.lackOfAutomation]: {
      advice: lackOfAutomationYellowAdvice,
      questions: [] as string[],
    },
    [hiringIssueMap.longCyclesWithDropoff]: {
      advice: null,
      questions: [] as string[],
    },
    [hiringIssueMap.poorStakeholderCommunication]: {
      advice: null,
      questions: [] as string[],
    },
    [hiringIssueMap.schedulingConflicts]: {
      advice: schedulingConflictsYellowAdvice,
      questions: [] as string[],
    },
  },
  [flagColorMap.green]: {
    [hiringIssueMap.inconsistentProcess]: {
      advice: inconsistentProcessGreenAdvice,
      questions: [] as string[],
    },
    [hiringIssueMap.lackOfAutomation]: {
      advice: lackOfAutomationGreenAdvice,
      questions: [] as string[],
    },
    [hiringIssueMap.longCyclesWithDropoff]: {
      advice: longCyclesWithDropoffGreenAdvice,
      questions: [] as string[],
    },
    [hiringIssueMap.poorStakeholderCommunication]: {
      advice: poorStakeholderCommunicationGreenAdvice,
      questions: [] as string[],
    },
    [hiringIssueMap.schedulingConflicts]: {
      advice: schedulingConflictsGreenAdvice,
      questions: [] as string[],
    },
  },
}

const hiringDisplayKeys = Object.keys(hiringIssueDisplayMap)

export const isFlagKey = (key: string): boolean => {
  // Check if the key is a HiringIssue
  const isHiringIssue = hiringDisplayKeys.includes(key)

  return isHiringIssue
}

export const getFlagInfo = (
  flag: FlagKey,
  value: any,
): { flagColor: FlagColor; flagValue: FlagTracking } | undefined => {
  const flagColor = flagKeyToFlagColor?.[flag]?.[value]
  const flagValue = defaultFlagMap?.[flagColor]?.[flag]
  return flagColor && flagValue ? { flagColor, flagValue } : undefined
}
